<template>
  <b-container fluid class="py-4 profile-page">
    <b-row class="justify-content-center">
      <b-col lg="12">

        <b-row>
          <!-- LEFT: personal card -->
          <b-col lg="4" class="mb-4">
            <b-card class="h-100 profile-card border-0">
              <div class="card-head d-flex align-items-center justify-content-between mb-3">
                <div class="section-kicker">Личный</div>
                <span class="mini-chip">
                  <b-icon icon="shield-check" class="mr-2"></b-icon>
                  Защищено
                </span>
              </div>

              <div class="d-flex flex-column align-items-center mb-3">
                <div class="profile-avatar mb-3 position-relative">
                  <div class="avatar-circle d-flex align-items-center justify-content-center">
                    <b-icon icon="person" scale="1.7"></b-icon>
                  </div>
                  <span class="badge badge-success shield-badge">
                    <b-icon icon="shield-check" scale="0.8"></b-icon>
                  </span>
                </div>

                <div class="user-name text-center">
                  {{ loginedUser && loginedUser.userName ? loginedUser.userName : 'Пользователь' }}
                </div>

                <div class="user-sub text-center mt-1">
                  Профиль • Настройки безопасности
                </div>
              </div>

              <div class="info-list">
                <div class="info-row">
                  <div class="info-k">Статус идентификации</div>
                  <div class="info-v">
                    <span :class="['status-pill', identificationClass]">
                      {{ identificationStatus }}
                    </span>
                  </div>
                </div>

                <div class="info-row">
                  <div class="info-k">Статус классификации</div>
                  <div class="info-v">
                    <span :class="['status-pill', classificationClass]">
                      {{ classificationStatus }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="mt-3">
                <div class="hint">
                  <b-icon icon="info-circle" class="mr-2"></b-icon>
                  Данные профиля доступны только вам и системе.
                </div>
              </div>
            </b-card>
          </b-col>

          <!-- RIGHT: profile & security -->
          <b-col lg="8" class="mb-4">
            <b-card class="border-0 profile-main-card mb-3">
              <div class="d-flex justify-content-between align-items-start align-items-md-center mb-3 flex-wrap">
                <div>
                  <div class="main-title">Профиль и Безопасность</div>
                  <div class="main-sub">
                    Управляйте контактными данными и параметрами безопасности аккаунта.
                  </div>
                </div>

                <div class="right-badge mt-3 mt-md-0">
                  <b-icon icon="lock-fill" class="mr-2"></b-icon>
                  Рекомендуется обновлять пароль регулярно
                </div>
              </div>

              <b-row>
                <!-- Email -->
                <b-col md="6" class="mb-3">
                  <div class="sub-card p-3 h-100">
                    <div class="sub-head">
                      <div class="sub-kicker">Эл. почта</div>
                      <b-button
                        size="sm"
                        variant="outline-light"
                        class="mini-btn"
                        @click="copyToClipboard(getUserEmail())"
                        :disabled="!getUserEmail()"
                      >
                        <b-icon icon="clipboard" class="mr-1"></b-icon>
                        Копировать
                      </b-button>
                    </div>

                    <div class="sub-value mono mt-2">
                      {{ getUserEmail() || '—' }}
                    </div>

                    <div class="sub-hint mt-2">
                      Используется для входа и уведомлений.
                    </div>
                  </div>
                </b-col>

                <!-- Password -->
                <b-col md="6" class="mb-3">
                  <div class="sub-card p-3 h-100 d-flex flex-column justify-content-between">
                    <div>
                      <div class="sub-kicker">Пароль</div>
                      <div class="sub-value mt-2">
                        ••••••••••
                      </div>
                      <div class="sub-hint mt-2">
                        Для защиты аккаунта используйте сложный пароль.
                      </div>
                    </div>
                    <div class="text-right mt-3">
                      <b-button size="sm" variant="primary" class="cta-btn px-3"
                                @click="openChangePassword">
                        ИЗМЕНИТЬ
                      </b-button>
                    </div>
                  </div>
                </b-col>
              </b-row>

              <!-- Change-password modal. Three fields with a client-side
                   confirm-match check before hitting the backend; bcrypt
                   verification of the current password happens server-side. -->
              <b-modal
                id="change-password-modal"
                ref="changePasswordModal"
                title="Изменение пароля"
                ok-title="Сохранить"
                ok-variant="primary"
                cancel-title="Отмена"
                cancel-variant="outline-secondary"
                :ok-disabled="passwordBusy"
                @ok="submitChangePassword"
                @hidden="resetPasswordForm"
                centered
              >
                <b-form-group label="Текущий пароль" label-class="reg-label">
                  <b-form-input type="password" v-model="pwForm.current" required />
                </b-form-group>
                <b-form-group label="Новый пароль (минимум 8 символов)" label-class="reg-label">
                  <b-form-input type="password" v-model="pwForm.next" required />
                </b-form-group>
                <b-form-group label="Подтвердите новый пароль" label-class="reg-label">
                  <b-form-input type="password" v-model="pwForm.confirm" required />
                </b-form-group>
                <b-alert
                  :show="pwForm.next.length > 0 && pwForm.confirm.length > 0 && pwForm.next !== pwForm.confirm"
                  variant="danger"
                  class="mb-0 py-2"
                >
                  Пароли не совпадают.
                </b-alert>
              </b-modal>

              <!-- 2FA mini-section. Full management lives on /security. -->
              <b-row>
                <b-col md="12" class="mb-3">
                  <div class="sub-card p-3 h-100">
                    <div class="sub-head">
                      <div class="sub-kicker">Двухфакторная аутентификация</div>
                      <span
                        class="badge"
                        :class="twoFA.enabled ? 'badge-success' : 'badge-secondary'"
                      >
                        {{ twoFA.enabled ? 'Включена' : 'Не включена' }}
                      </span>
                    </div>

                    <div class="sub-value mt-2" v-if="twoFA.enabled">
                      Дополнительный код из приложения требуется при каждом входе.
                      Резервных кодов осталось: <strong>{{ twoFA.backupCodesLeft }}</strong>.
                    </div>
                    <div class="sub-value mt-2" v-else>
                      Защитите вход в аккаунт одноразовым кодом из Google
                      Authenticator или совместимого приложения.
                    </div>

                    <div class="sub-hint mt-2" v-if="twoFA.enabled && !twoFA.canDisable">
                      <b-icon icon="info-circle" class="mr-1"></b-icon>
                      2FA обязательна и не может быть отключена.
                    </div>

                    <div class="text-right mt-3">
                      <b-button size="sm" variant="primary" class="cta-btn px-3"
                                @click="$router.push('/security')">
                        {{ twoFA.enabled ? 'УПРАВЛЯТЬ' : 'ВКЛЮЧИТЬ' }}
                      </b-button>
                    </div>
                  </div>
                </b-col>
              </b-row>

              <!-- Qualified-client SoF reminder. Visible when the user
                   already passed the qualified test but hasn't been
                   promoted to RETAIL_Q yet — i.e. SoF docs either not
                   uploaded or awaiting manager review. Hidden once
                   client_category flips to RETAIL_Q / PROFESSIONAL. -->
              <b-row v-if="needsSofUpload">
                <b-col md="12" class="mb-3">
                  <div class="sub-card p-3 h-100 sof-reminder-card">
                    <div class="sub-head">
                      <div class="sub-kicker">Квалифицированный статус</div>
                      <span class="badge badge-warning">Требуется действие</span>
                    </div>
                    <div class="sub-value mt-2">
                      Тест пройден. Чтобы получить статус
                      <strong>Розничный (квалифицированный)</strong> и поднять
                      лимит до 100 000 USD/мес — загрузите документы,
                      подтверждающие источник средств.
                    </div>
                    <div class="sub-hint mt-2">
                      Если уже загрузили — ожидайте подтверждения менеджера.
                    </div>
                    <div class="text-right mt-3">
                      <b-button size="sm" variant="primary" class="cta-btn px-3"
                                @click="$router.push('/qualified')">
                        ЗАГРУЗИТЬ ДОКУМЕНТЫ
                      </b-button>
                    </div>
                  </div>
                </b-col>
              </b-row>

              <!-- Optional: Security tip block -->
              <div class="tip-card p-3 mt-1">
                <div class="d-flex align-items-start">
                  <div class="tip-ico mr-3">
                    <b-icon icon="shield-lock" scale="1.2"></b-icon>
                  </div>
                  <div>
                    <div class="tip-title">Совет по безопасности</div>
                    <div class="tip-text">
                      Не передавайте код подтверждения и данные входа третьим лицам.
                    </div>
                  </div>
                </div>
              </div>

            </b-card>

            <!-- My documents — every artefact this user has on file across
                 all upgrade tracks, with a download shortcut so they don't
                 have to retrace the wizard. -->
            <b-card class="border-0 profile-main-card mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <div class="main-title">Мои документы</div>
                  <div class="main-sub">
                    Все документы, которые вы загружали ранее.
                  </div>
                </div>
                <b-button
                  size="sm"
                  variant="outline-light"
                  :disabled="docsLoading"
                  @click="fetchMyDocs"
                  title="Обновить список"
                >
                  <b-icon icon="arrow-repeat" :animation="docsLoading ? 'spin' : null" />
                </b-button>
              </div>
              <div v-if="docsLoading" class="sub-card p-3">
                <b-spinner small class="mr-2" />
                <span class="sub-value">Загрузка…</span>
              </div>
              <div v-else-if="!myDocs.length" class="sub-card p-3">
                <span class="sub-value" style="color: rgba(229,231,235,0.7);">Пока ничего не загружено.</span>
              </div>
              <div v-else class="docs-table-wrap">
                <table class="docs-table">
                  <thead>
                    <tr>
                      <th>Категория</th>
                      <th>Тип</th>
                      <th>Дата</th>
                      <th>Статус</th>
                      <th>Комментарий</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(doc, idx) in myDocs" :key="doc.docId != null ? doc.docId : idx">
                      <td>
                        <span :class="['docs-track', 'docs-track-' + docTrack(doc.docType).code]">
                          {{ docTrack(doc.docType).label }}
                        </span>
                      </td>
                      <td>
                        <div class="docs-type">{{ docTypeLabel(doc.docType) }}</div>
                        <div class="docs-filename mono">{{ docFileName(doc.docPath) }}</div>
                      </td>
                      <td class="docs-date">{{ formatDocDate(doc.createDate) }}</td>
                      <td>
                        <span :class="['docs-status', 'docs-status-' + (doc.docStatus || '').toLowerCase()]">
                          {{ doc.docStatus || '—' }}
                        </span>
                      </td>
                      <td class="docs-comment">{{ doc.mlroDesc || '—' }}</td>
                      <td>
                        <b-button size="sm" variant="outline-light" @click="downloadDoc(doc)" title="Скачать">
                          <b-icon icon="download" />
                        </b-button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </b-card>

            <!-- Survey result -->
            <b-card class="border-0 profile-main-card mb-3">
              <div class="main-title mb-2">Результаты анкеты</div>
              <div class="main-sub mb-3">
                Данные анкеты (источник средств, опыт, объём операций).
              </div>
              <div v-if="surveyLoading" class="sub-card p-3">
                <b-spinner small class="mr-2" />
                <span class="sub-value">Загрузка…</span>
              </div>
              <div v-else-if="!surveyHasAny" class="sub-card p-3">
                <span class="sub-value" style="color: rgba(229,231,235,0.7);">Данные анкеты пока не заполнены.</span>
              </div>
              <div v-else class="info-list">
                <div class="info-row">
                  <div class="info-k">Источник средств</div>
                  <div class="info-v">{{ surveySourceOfFundsLabel || '—' }}</div>
                </div>
                <div class="info-row">
                  <div class="info-k">Опыт работы с цифровыми активами</div>
                  <div class="info-v">{{ surveyExperienceLabel || '—' }}</div>
                </div>
                <div class="info-row">
                  <div class="info-k">Планируемый объём операций в месяц</div>
                  <div class="info-v">{{ surveyPlannedVolumeLabel || '—' }}</div>
                </div>
                <div v-if="surveyAttrsDisplay" class="info-row">
                  <div class="info-k">Доп. атрибуты</div>
                  <div class="info-v">{{ surveyAttrsDisplay }}</div>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>

      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapState } from 'vuex'
import axios from 'axios'
import { apiDomain, getHeader } from '@/config/config'

const SOURCE_OF_FUNDS_LABELS = {
  SALARY: 'заработная плата',
  ENTREPRENEURIAL_ACTIVITY: 'предпринимательская деятельность',
  INVESTMENT_INCOME: 'инвестиционный доход',
  OTHER: 'иное'
}
const EXPERIENCE_LABELS = {
  NO_EXPERIENCE: 'нет опыта',
  LESS_THAN_1_YEAR: 'менее 1 года',
  ONE_TO_THREE_YEARS: '1–3 года',
  MORE_THAN_3_YEARS: 'более 3 лет'
}
const PLANNED_VOLUME_LABELS = {
  UP_TO_1000_USD: 'до 1 000 USD',
  FROM_1000_TO_100000_USD: '1 000–100 000 USD',
  MORE_THAN_100000_USD: 'более 100 000 USD'
}

export default {
  name: 'ProfilePage',
  data() {
    return {
      lastLogin: 'Сегодня, 12:34',
      loadedUserData: {},
      surveyLoading: false,
      pwForm: { current: '', next: '', confirm: '' },
      passwordBusy: false,
      // Unified document history shown in the "Мои документы" card.
      // Filled by GET /ptest/mydocs.
      myDocs: [],
      docsLoading: false,
    }
  },
  computed: {
    ...mapState(['loginedUser']),
    // Onboarding status from lms_core_user.onboarding_status — surfaced
    // as a human label + pill colour. APPROVED is the green happy path,
    // REJECTED is red, PENDING/NOTIDENTIFIED muted grey.
    identificationStatus() {
      const st = (this.loadedUserData && this.loadedUserData.onboardingStatus) || 'NOTIDENTIFIED'
      const map = {
        NOTIDENTIFIED: 'Не пройдена',
        PENDING:       'На проверке',
        APPROVED:      'Пройдена',
        REJECTED:      'Отклонена',
      }
      return map[st] || st
    },
    identificationClass() {
      const st = (this.loadedUserData && this.loadedUserData.onboardingStatus) || 'NOTIDENTIFIED'
      if (st === 'APPROVED') return 'pill-good'
      if (st === 'REJECTED') return 'pill-bad'
      return 'pill-muted'
    },
    // The four classification states that map to lms_core_user.client_category.
    // We surface a human label + a pill colour: green for any real client
    // category, muted grey while still NONE.
    classificationStatus() {
      const cat = (this.loadedUserData && this.loadedUserData.clientCategory) || 'NONE'
      const map = {
        NONE: 'Не назначен',
        RETAIL_NQ: 'Розничный (неквалифицированный)',
        RETAIL_Q: 'Розничный (квалифицированный)',
        PROFESSIONAL: 'Профессиональный',
      }
      return map[cat] || cat
    },
    classificationClass() {
      const cat = (this.loadedUserData && this.loadedUserData.clientCategory) || 'NONE'
      return cat === 'NONE' ? 'pill-muted' : 'pill-good'
    },
    // True when the user passed the qualified test but the manager
    // hasn't promoted them to RETAIL_Q yet — meaning SoF documents are
    // either missing or pending review.
    needsSofUpload() {
      const d = this.loadedUserData || {}
      const passed = d.isPassedQualified === 1 || d.isPassedQualified === true
      const cat = d.clientCategory
      return passed && cat !== 'RETAIL_Q' && cat !== 'PROFESSIONAL'
    },
    surveySourceOfFundsLabel() {
      const v = this.loadedUserData.sourceOfFunds || (this.loginedUser && this.loginedUser.sourceOfFunds)
      return v ? (SOURCE_OF_FUNDS_LABELS[v] || v) : ''
    },
    surveyExperienceLabel() {
      const v = this.loadedUserData.experience || (this.loginedUser && this.loginedUser.experience)
      return v ? (EXPERIENCE_LABELS[v] || v) : ''
    },
    surveyPlannedVolumeLabel() {
      const v = this.loadedUserData.plannedVolume || (this.loginedUser && this.loginedUser.plannedVolume)
      return v ? (PLANNED_VOLUME_LABELS[v] || v) : ''
    },
    surveyAttrsDisplay() {
      const attrs = this.loadedUserData.attrs || (this.loginedUser && this.loginedUser.attrs)
      if (attrs == null) return ''
      if (typeof attrs === 'string') return attrs
      if (typeof attrs === 'object' && !Array.isArray(attrs)) {
        return Object.entries(attrs)
          .map(([k, v]) => `${k}: ${v == null ? '' : v}`)
          .join('; ') || '—'
      }
      return String(attrs)
    },
    surveyHasAny() {
      return !!(
        this.surveySourceOfFundsLabel ||
        this.surveyExperienceLabel ||
        this.surveyPlannedVolumeLabel ||
        this.surveyAttrsDisplay
      )
    },
    // 2FA card state, derived from the same /exchange/userdetails payload
    // that fills loadedUserData. Backend ships totpEnabled / totpBackupCodesLeft
    // / totpCanDisable inline so we don't need a second status round-trip.
    twoFA() {
      const d = this.loadedUserData || {}
      return {
        enabled: d.totpEnabled === true,
        backupCodesLeft: typeof d.totpBackupCodesLeft === 'number' ? d.totpBackupCodesLeft : 0,
        canDisable: d.totpCanDisable !== false,
      }
    },
  },
  mounted() {
    // 2FA status now ships inside /exchange/userdetails — no separate
    // POST /api/2fa/status round-trip needed.
    this.fetchUserDetails()
    this.fetchMyDocs()
  },
  methods: {
    fetchMyDocs() {
      // Single endpoint that returns every document this user has on
      // file across all upgrade tracks. Errors leave the list empty.
      this.docsLoading = true
      axios.get(`${apiDomain}/ptest/mydocs`, { headers: getHeader() })
        .then(res => {
          const d = res.data
          this.myDocs = Array.isArray(d) ? d : (d && (d.list || d.data)) || []
        })
        .catch(() => { this.myDocs = [] })
        .finally(() => { this.docsLoading = false })
    },
    docTypeLabel(t) {
      // Human-readable doc kind. Track ("Quals" / "Professional") lives in
      // its own column now (see docTrack), so the labels themselves are
      // only describing *what's inside* the file.
      const map = {
        QUALIFIED:            'Подтверждение источника средств',
        CERTIFICATE:          'Сертификат CFA / FRM / CAIA / CIIA',
        PROFESSIONAL_HISTORY: 'Торговая история',
        PROFESSIONAL_SOF:     'Подтверждение источника средств',
        PROFESSIONAL_CERT:    'Сертификат / диплом',
        // Legacy single-archive upload (pre-stepper). The zip bundled SoF
        // + history together, so we can't be more specific than "архив".
        PROFESSIONAL:         'Архив документов (SoF + история)',
      }
      return map[t] || t || '—'
    },
    docTrack(t) {
      // Which upgrade track produced the doc — Retail-Qualified vs
      // Professional vs everything else. Surfaced as its own column so
      // two PROFESSIONAL legacy archives stop looking identical.
      if (t === 'QUALIFIED' || t === 'CERTIFICATE') {
        return { code: 'quals', label: 'Retail Qualified' }
      }
      if (t === 'PROFESSIONAL'
        || t === 'PROFESSIONAL_HISTORY'
        || t === 'PROFESSIONAL_SOF'
        || t === 'PROFESSIONAL_CERT') {
        return { code: 'pro', label: 'Professional' }
      }
      return { code: 'other', label: '—' }
    },
    formatDocDate(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (isNaN(d.getTime())) return '—'
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ` +
             `${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    docFileName(path) {
      if (!path) return ''
      const parts = String(path).split(/[\\/]/)
      return parts[parts.length - 1] || ''
    },
    downloadDoc(doc) {
      const fileName = this.docFileName(doc && doc.docPath)
      if (!fileName) return
      const url = `${apiDomain}/ptest/downloadclientfile/${encodeURIComponent(fileName)}`
      axios.get(url, { headers: getHeader(), responseType: 'blob' })
        .then(response => {
          const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = fileName
          link.click()
          window.URL.revokeObjectURL(link.href)
        })
        .catch(() => {
          if (this.$bvToast) {
            this.$bvToast.toast('Не удалось скачать файл.', {
              variant: 'danger', title: 'Ошибка', solid: true,
              toaster: 'b-toaster-top-center', autoHideDelay: 4000,
            })
          }
        })
    },
    openChangePassword() {
      this.resetPasswordForm()
      this.$bvModal.show('change-password-modal')
    },
    resetPasswordForm() {
      this.pwForm = { current: '', next: '', confirm: '' }
      this.passwordBusy = false
    },
    submitChangePassword(evt) {
      // Vue-bootstrap fires this on the OK button. preventDefault keeps the
      // modal open while we validate / send, and we hide() ourselves on
      // success.
      evt.preventDefault()
      if (!this.pwForm.current || !this.pwForm.next || !this.pwForm.confirm) {
        this.pwToast('Заполните все поля.', 'danger')
        return
      }
      if (this.pwForm.next.length < 8) {
        this.pwToast('Новый пароль должен быть не короче 8 символов.', 'danger')
        return
      }
      if (this.pwForm.next !== this.pwForm.confirm) {
        this.pwToast('Новый пароль и подтверждение не совпадают.', 'danger')
        return
      }
      this.passwordBusy = true
      axios
        .post(
          `${apiDomain}/user/changepassword`,
          { currentPassword: this.pwForm.current, newPassword: this.pwForm.next },
          { headers: getHeader() }
        )
        .then(() => {
          this.pwToast('Пароль успешно изменён.', 'success')
          this.$bvModal.hide('change-password-modal')
        })
        .catch(err => {
          const body = err && err.response && err.response.data
          const txt = typeof body === 'string' ? body : ''
          if (txt.includes('bad-current')) {
            this.pwToast('Текущий пароль введён неверно.', 'danger')
          } else if (txt.includes('weak-password')) {
            this.pwToast('Новый пароль слишком короткий — минимум 8 символов.', 'danger')
          } else {
            this.pwToast('Не удалось сменить пароль. Попробуйте ещё раз.', 'danger')
          }
        })
        .finally(() => { this.passwordBusy = false })
    },
    pwToast(message, variant) {
      if (!this.$bvToast) return
      this.$bvToast.toast(message, {
        title: variant === 'success' ? 'Готово' : 'Ошибка',
        variant,
        autoHideDelay: 4500,
        toaster: 'b-toaster-top-center',
        solid: true,
      })
    },
    fetchUserDetails() {
      if (!this.loginedUser || (this.loginedUser.id == null && this.loginedUser.userId == null)) return
      this.surveyLoading = true
      const id = this.loginedUser.id != null ? this.loginedUser.id : this.loginedUser.userId
      axios
        .get(`${apiDomain}/exchange/userdetails/${id}`, { headers: getHeader() })
        .then(res => {
          this.loadedUserData = res.data || {}
        })
        .catch(() => {
          this.loadedUserData = {}
        })
        .finally(() => {
          this.surveyLoading = false
        })
    },
    getUserEmail() {
      return this.loginedUser && this.loginedUser.userName ? String(this.loginedUser.userName) : ''
    },
    copyToClipboard(text) {
      var value = text ? String(text) : ''
      if (!value) {
        this.toast('Нечего копировать.', 'Внимание', 'warning')
        return
      }

      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(
          () => this.toast('Скопировано в буфер обмена.', 'Готово', 'success'),
          () => this.fallbackCopy(value)
        )
        return
      }
      this.fallbackCopy(value)
    },
    fallbackCopy(value) {
      try {
        var el = document.createElement('textarea')
        el.value = value
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        this.toast('Скопировано в буфер обмена.', 'Готово', 'success')
      } catch (e) {
        this.toast('Не удалось скопировать. Скопируйте вручную.', 'Ошибка', 'danger')
      }
    },
    toast(message, title, variant) {
      if (this.$bvToast) {
        this.$bvToast.toast(message, {
          title: title || 'Сообщение',
          variant: variant || 'info',
          solid: true,
          toaster: 'b-toaster-top-center',
          autoHideDelay: 2600
        })
      }
    }
  }
}
</script>

<style scoped>
/* ===== Dark page background ===== */
.profile-page {
  min-height: 100vh;
  
}

/* ===== Cards (no white) ===== */
.profile-card,
.profile-main-card {
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03));
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: 0 18px 60px rgba(0,0,0,0.35);
}

/* Header mini elements */
.section-kicker{
  color: rgba(229,231,235,0.75);
  font-weight: 900;
  font-size: 12px;
  letter-spacing: .35px;
  text-transform: uppercase;
}
.mini-chip{
  display: inline-flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: 999px;
  background: rgba(34,197,94,0.10);
  border: 1px solid rgba(34,197,94,0.20);
  color: #d1fae5;
  font-weight: 900;
  font-size: 12px;
}

/* Avatar */
.profile-avatar .avatar-circle {
  width: 86px;
  height: 86px;
  border-radius: 50%;
  background: rgba(99,102,241,0.12);
  border: 1px solid rgba(99,102,241,0.22);
  color: #c7d2fe;
  box-shadow: 0 14px 30px rgba(99,102,241,0.18);
}

.shield-badge {
  position: absolute;
  bottom: 4px;
  right: 6px;
  border-radius: 999px;
  padding: 5px 7px;
}

/* User text */
.user-name{
  color: #f3f4f6;
  font-weight: 900;
  font-size: 16px;
}
.user-sub{
  color: rgba(229,231,235,0.70);
  font-size: 12px;
  font-weight: 700;
}

/* Info list (custom, not white list-group) */
.info-list{
  margin-top: 10px;
  border-radius: 16px;
  overflow: hidden;
  background: rgba(0,0,0,0.18);
  border: 1px solid rgba(255,255,255,0.08);
}
.info-row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding: 12px 14px;
  border-top: 1px solid rgba(255,255,255,0.06);
}
.info-row:first-child{ border-top: 0; }
.info-k{
  color: rgba(229,231,235,0.70);
  font-weight: 900;
  font-size: 12px;
}
.info-v{
  color: #f3f4f6;
  font-weight: 900;
  font-size: 12px;
}
.link-soft{
  color: #93c5fd !important;
  font-weight: 900;
  text-decoration: none !important;
}
.link-soft:hover{ color: #bfdbfe !important; }

/* Status pill */
.status-pill{
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 12px;
  border: 1px solid rgba(255,255,255,0.10);
  background: rgba(255,255,255,0.03);
}
.pill-muted{
  color: rgba(229,231,235,0.75);
}
.pill-good{
  color: #d1fae5;
  background: rgba(34,197,94,0.10);
  border-color: rgba(34,197,94,0.22);
}
.pill-bad{
  color: #fecaca;
  background: rgba(239,68,68,0.10);
  border-color: rgba(239,68,68,0.32);
}

/* Right header */
.main-title{
  color: #f3f4f6;
  font-weight: 950;
  font-size: 18px;
}
.main-sub{
  color: rgba(229,231,235,0.70);
  font-size: 12px;
  font-weight: 700;
  margin-top: 4px;
}
.right-badge{
  display:inline-flex;
  align-items:center;
  padding: 8px 10px;
  border-radius: 14px;
  background: rgba(251,191,36,0.10);
  border: 1px solid rgba(251,191,36,0.20);
  color: #fde68a;
  font-weight: 900;
  font-size: 12px;
}

/* Sub-cards (dark) */
.sub-card{
  border-radius: 16px;
  background: rgba(0,0,0,0.18);
  border: 1px solid rgba(255,255,255,0.08);
}
.sub-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap: 10px;
}
.sub-kicker{
  color: rgba(229,231,235,0.70);
  font-weight: 900;
  font-size: 12px;
  letter-spacing: .2px;
  text-transform: uppercase;
}
.sub-value{
  color: #f3f4f6;
  font-weight: 900;
  font-size: 14px;
}
.sub-hint{
  color: rgba(229,231,235,0.68);
  font-size: 12px;
  font-weight: 700;
}
.mono{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

/* Buttons */
.mini-btn{
  border-radius: 14px !important;
  font-weight: 900 !important;
  border: 1px solid rgba(255,255,255,0.16) !important;
  background: rgba(255,255,255,0.03) !important;
  color: #e5e7eb !important;
}
.mini-btn:hover{
  background: rgba(255,255,255,0.06) !important;
  transform: translateY(-1px);
}
.cta-btn{
  border-radius: 14px !important;
  font-weight: 950 !important;
  box-shadow: 0 18px 40px rgba(99,102,241,0.18);
}

/* Tip card */
.tip-card{
  border-radius: 16px;
  background: rgba(99,102,241,0.10);
  border: 1px solid rgba(99,102,241,0.20);
}
.tip-ico{
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display:flex;
  align-items:center;
  justify-content:center;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.08);
  color: #c7d2fe;
}
.tip-title{
  color: #f3f4f6;
  font-weight: 950;
  font-size: 13px;
}
.tip-text{
  color: rgba(229,231,235,0.72);
  font-size: 12px;
  font-weight: 700;
  margin-top: 4px;
}

/* Hint */
.hint{
  color: rgba(229,231,235,0.70);
  font-size: 12px;
  font-weight: 700;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  padding: 10px 12px;
  border-radius: 14px;
}

.sof-reminder-card {
  border: 1px solid rgba(239, 185, 20, 0.35);
  background: linear-gradient(135deg, rgba(239, 185, 20, 0.10), rgba(239, 185, 20, 0.03));
}

/* ===== My documents table ===== */
.docs-table-wrap { overflow-x: auto; }
.docs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.docs-table th {
  text-align: left;
  font-weight: 700;
  color: rgba(229,231,235,0.55);
  padding: 8px 10px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 11px;
}
.docs-table td {
  padding: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  vertical-align: top;
  color: rgba(229,231,235,0.85);
}
.docs-type { font-weight: 700; color: #f1f5f9; }
.docs-filename {
  font-size: 11px;
  color: rgba(229,231,235,0.55);
  word-break: break-all;
  margin-top: 2px;
}
.docs-comment { color: rgba(229,231,235,0.65); font-size: 12px; }
.docs-status {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.8);
}
.docs-status-pending  { background: rgba(239, 185, 20, 0.18); color: #EFB914; }
.docs-status-approved { background: rgba(76, 175, 80, 0.18);  color: #66BB6A; }
.docs-status-rejected { background: rgba(239, 83, 80, 0.18);  color: #EF5350; }
.docs-track {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.03em;
  white-space: nowrap;
}
.docs-track-quals { background: rgba(96, 165, 250, 0.16); color: #93C5FD; }
.docs-track-pro   { background: rgba(168, 85, 247, 0.18); color: #C084FC; }
.docs-track-other { background: rgba(255,255,255,0.08);   color: rgba(229,231,235,0.7); }
.docs-date { color: rgba(229,231,235,0.7); font-size: 12px; white-space: nowrap; }
.mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
</style>
